import * as Sentry from '@sentry/capacitor';
import { init } from '@sentry/react';

import { USE_EMULATOR, SENTRY_DSN, SENTRY_ENV, API_URL } from '../env';

// Fields to filter from network requests/responses and console logs
const SENSITIVE_FIELDS = ['ssn', 'fullSsn'];

// Helper function to recursively filter sensitive fields from objects
const filterSensitiveData = (data: any): any => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(filterSensitiveData);
  }

  const filtered: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_FIELDS.includes(key)) {
      filtered[key] = '[Filtered]';
    } else if (typeof value === 'object' && value !== null) {
      filtered[key] = filterSensitiveData(value);
    } else {
      filtered[key] = value;
    }
  }
  return filtered;
};

/**
 * Returns true if a Sentry event is the known-benign ChirpLink teardown error.
 *
 * Chirp's bundled SDK (chirplink.js) closes its POPUP overlay with
 * `document.body.removeChild(this.chirpVerification)`. When `close()` runs and
 * the node is already detached (e.g. a double-close), `removeChild` throws a
 * NotFoundError ("The object can not be found here." in WebKit / "...is not a
 * child of this node." in Chromium). The overlay is already gone, so the throw
 * is cosmetic — but it's an uncaught exception that floods Sentry.
 *
 * We drop it ONLY when BOTH hold:
 *   1. the error message matches this specific removeChild failure, and
 *   2. a stack frame originates from Chirp's script.
 * The stack-frame requirement is the safety guard: a NotFoundError of the same
 * wording thrown from our own code (a real bug) still gets reported.
 */
const isBenignChirpTeardownError = (event: Sentry.Event): boolean => {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;

  const message = `${exception.type ?? ''}: ${exception.value ?? ''}`;
  const matchesMessage = /object can ?not be found here/i.test(message) || /not a child of this node/i.test(message);
  if (!matchesMessage) return false;

  const frames = exception.stacktrace?.frames ?? [];
  return frames.some((frame) => /chirplink|chirp\.digital/i.test(frame.filename ?? ''));
};

export const replay = Sentry.replayIntegration({
  maskAllText: false,
  blockAllMedia: false,
  maskAllInputs: false,

  // Enable detailed network recording
  networkDetailAllowUrls: [/.*/], // Capture all network requests
  networkCaptureBodies: true,
  networkRequestHeaders: ['accept', 'authorization', 'content-type', 'user-agent', 'x-requested-with'],
  networkResponseHeaders: ['content-type', 'content-length', 'date', 'server', 'x-request-id'],

  // Filter sensitive data from network request/response bodies and console logs
  beforeAddRecordingEvent: (event) => {
    // Filter network request/response bodies
    if (event.data && 'payload' in event.data) {
      const payload = event.data.payload as any;

      if (payload?.request?.body) {
        try {
          const body = JSON.parse(payload.request.body);
          payload.request.body = JSON.stringify(filterSensitiveData(body));
        } catch {
          // Not JSON, leave as-is
        }
      }

      if (payload?.response?.body) {
        try {
          const body = JSON.parse(payload.response.body);
          payload.response.body = JSON.stringify(filterSensitiveData(body));
        } catch {
          // Not JSON, leave as-is
        }
      }
    }

    // Filter console messages
    if (event.data && 'payload' in event.data) {
      const payload = event.data.payload as any;

      if (payload?.payload && Array.isArray(payload.payload)) {
        payload.payload = payload.payload.map((arg: any) => {
          if (typeof arg === 'object' && arg !== null) {
            return filterSensitiveData(arg);
          }
          return arg;
        });
      }
    }

    return event;
  },
});

Sentry.init(
  {
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    sendDefaultPii: true,
    replaysSessionSampleRate: 1,
    integrations: [replay],
    // tunnel: `${API_URL}/v1/tunnel`,

    // Drop the known-benign ChirpLink overlay-teardown NotFoundError. Returning
    // null discards the event; anything else is reported unchanged.
    beforeSend(event) {
      if (isBenignChirpTeardownError(event)) {
        return null;
      }
      return event;
    },

    // Filter sensitive fields from breadcrumbs (console logs, etc.)
    beforeBreadcrumb(breadcrumb) {
      // Filter sensitive data from console breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.data) {
        breadcrumb.data = filterSensitiveData(breadcrumb.data);
      }

      // Filter sensitive data from fetch/xhr breadcrumbs
      if ((breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') && breadcrumb.data) {
        breadcrumb.data = filterSensitiveData(breadcrumb.data);
      }

      return breadcrumb;
    },
  },
  init,
);

interface IUpdateScope {
  id?: string;
  isPremium?: boolean;
  [key: string]: string | number | boolean;
}

export const updateScope = (identity: IUpdateScope) => {
  const scope = Sentry.getCurrentScope();

  if (identity.id) {
    scope.setUser({ id: identity.id });
  }

  if (identity.isPremium) {
    scope.setTag('subscription_type', identity.isPremium ? 'PREMIUM' : 'FREE');
  }

  for (const [key, value] of Object.entries(identity)) {
    if (key === 'id' || key === 'isPremium') continue;

    scope.setExtra(key, value);
  }
};

export const clearScope = () => {
  const scope = Sentry.getCurrentScope();

  scope.clear();
};

export const captureException = (e: Error, context?: any) => {
  if (USE_EMULATOR) {
    console.error(e);
  }

  Sentry.captureException(e, {
    contexts: { errorInfo: context || {} },
  });
};

export const captureMessage = (msg: string, context?: any) => {
  if (USE_EMULATOR) {
    console.log(msg);
  }

  Sentry.captureMessage(msg, {
    contexts: { state: context || {} },
    user: { id: context?.userId },
  });
};

export const capturePaymentAction = (action: string, context?: any) => {
  Sentry.setTag('payment_action', action);
  Sentry.addBreadcrumb({
    type: 'default',
    level: 'info',
    category: 'payment.action',
    message: `Payment action: ${action}`,
    data: context,
  });
};

/**
 * Fold the primitive (string/number/boolean) fields of a breadcrumb context into a compact
 * ` | key=val key=val` suffix. We append this to the breadcrumb *message* because the Sentry
 * replay/breadcrumb UI surfaces the message but keeps the structured `data` collapsed — so
 * without this the key facts are effectively invisible. Nested objects/arrays are left to `data`.
 */
const formatChirpContext = (context?: Record<string, unknown>): string => {
  if (!context) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}=${value}`);
    }
  }
  return parts.length ? ` | ${parts.join(' ')}` : '';
};

/**
 * Emit a Chirp SDK/flow lifecycle event. Verbose by design: support gets vague "not connecting"
 * reports and needs to reconstruct exactly what happened, so we log every SDK callback and every
 * decision we make (open/close/redirect/refresh/step change/error recovery).
 *
 * Each call does three things:
 *   1. console.info in ALL environments — Sentry's console-breadcrumb integration captures it, so
 *      it lands on the replay timeline and is visible in dev tools.
 *   2. folds the primitive context into the breadcrumb message so it shows without expanding `data`.
 *   3. keeps the full structured `context` on the `chirp.lifecycle` breadcrumb for fidelity.
 *
 * We intentionally do NOT scrub `context` (custom-category breadcrumbs bypass `beforeBreadcrumb`).
 * Nothing in the Chirp flow is sensitive today; if that changes we scrub reactively / file a Sentry
 * deletion request rather than losing visibility now.
 */
export const captureChirpEvent = (event: string, context?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.info(`[chirp] ${event}`, context ?? '');

  Sentry.addBreadcrumb({
    type: 'default',
    level: 'info',
    category: 'chirp.lifecycle',
    message: `Chirp: ${event}${formatChirpContext(context)}`,
    data: context,
  });
};

/**
 * Emit a native deep-link (`appUrlOpen`) lifecycle event. Any URL opened by the OS that isn't one
 * of our known ROUTE_PATHS falls through to `navigate('/')`, which — for an authenticated,
 * onboarded user — immediately bounces to /dashboard via HomeLayout's redirect. That can tear down
 * whatever screen the user was on (e.g. an in-progress loan application step) with no visible
 * cause in the UI, so every appUrlOpen needs a record of the raw URL and which branch it took.
 */
export const captureDeepLinkEvent = (event: string, context?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.info(`[deeplink] ${event}`, context ?? '');

  Sentry.addBreadcrumb({
    type: 'default',
    level: 'info',
    category: 'deeplink.appUrlOpen',
    message: `DeepLink: ${event}${formatChirpContext(context)}`,
    data: context,
  });
};

/**
 * Emit a router-guard redirect event. DashboardLayout and HomeLayout both render a bare
 * `<Navigate>` when their gating conditions trip, which unmounts whatever screen was on-screen
 * (e.g. mid loan-application flow) with only a generic "Navigation" breadcrumb from Sentry's
 * default URL tracking — no indication of *why*. This records which guard fired and the state it
 * evaluated, so a redirect can be attributed to a specific condition instead of inferred from gaps
 * in the timeline.
 */
export const captureRouteGuardRedirect = (event: string, context?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.info(`[route-guard] ${event}`, context ?? '');

  Sentry.addBreadcrumb({
    type: 'default',
    level: 'info',
    category: 'route.guard',
    message: `RouteGuard: ${event}${formatChirpContext(context)}`,
    data: context,
  });
};

/**
 * Emit an auth lifecycle event as a breadcrumb rather than a Sentry issue.
 *
 * Firebase surfaces "this identity already belongs to another account" as a thrown FirebaseError
 * (`auth/account-exists-with-different-credential` and friends). It's an expected outcome of a user
 * signing up twice, not a defect — every one of those codes has a dedicated UI path — so reporting
 * them via captureException just floods Sentry with unactionable issues. We keep a breadcrumb so
 * the code still shows on the session replay timeline when a real failure is being investigated.
 */
export const captureAuthEvent = (event: string, context?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.info(`[auth] ${event}`, context ?? '');

  Sentry.addBreadcrumb({
    type: 'default',
    level: 'info',
    category: 'auth.lifecycle',
    message: `Auth: ${event}${formatChirpContext(context)}`,
    data: context,
  });
};

export const captureWarning = (msg: string, context?: any) => {
  if (USE_EMULATOR) {
    console.warn(msg);
  }

  Sentry.captureMessage(msg, {
    level: 'warning',
    contexts: { state: context || {} },
    user: { id: context?.userId },
  });
};
